import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Brand } from '../types'

interface UseBrandsOptions {
  page?: number
  limit?: number
  search?: string
  ownershipType?: string
  hasLogo?: boolean | null
}

export function useBrands(client: SupabaseClient | null, options: UseBrandsOptions = {}) {
  const { page = 1, limit = 20, search = '', ownershipType, hasLogo } = options

  const [brands, setBrands] = useState<Brand[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBrands = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('brands')
        .select('*', { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`brand_name.ilike.%${search}%,parent_company_name.ilike.%${search}%`)
      }

      // Apply ownership type filter
      if (ownershipType) {
        query = query.eq('ownership_type', ownershipType)
      }

      // Apply logo filter
      if (hasLogo === true) {
        query = query.not('logo_url', 'is', null)
      } else if (hasLogo === false) {
        query = query.is('logo_url', null)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('brand_name', { ascending: true })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setBrands(data || [])
      setTotal(count || 0)
    } catch (err: any) {
      console.error('Error fetching brands:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, search, ownershipType, hasLogo])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  return { brands, total, isLoading, error, refetch: fetchBrands }
}

export function useBrandMutations(client: SupabaseClient | null) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createBrand = useCallback(async (brand: Partial<Brand>) => {
    if (!client) return null

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: insertError } = await client
        .from('brands')
        .insert(brand)
        .select()
        .single()

      if (insertError) throw insertError
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const updateBrand = useCallback(async (id: string, updates: Partial<Brand>) => {
    if (!client) return null

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await client
        .from('brands')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (updateError) throw updateError
      return data
    } catch (err: any) {
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [client])

  const deleteBrand = useCallback(async (id: string) => {
    if (!client) return false

    setIsLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await client
        .from('brands')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [client])

  return { createBrand, updateBrand, deleteBrand, isLoading, error }
}
